import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import StrategyBuilder from '@/components/strategy/StrategyBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategiesTab = () => {
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const response = await axios.get(`${API}/strategies`, { withCredentials: true });
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const createNewStrategy = () => {
    setSelectedStrategy(null);
    setShowBuilder(true);
  };

  const editStrategy = (strategy) => {
    setSelectedStrategy(strategy);
    setShowBuilder(true);
  };

  const deleteStrategy = async (strategyId) => {
    const strategy = strategies.find(s => s.id === strategyId);
    
    if (!window.confirm(`Are you sure you want to delete "${strategy?.name}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/strategies/${strategyId}`, { withCredentials: true });
      toast.success('Strategy deleted successfully');
      
      // Remove from list immediately for instant feedback
      setStrategies(prevStrategies => prevStrategies.filter(s => s.id !== strategyId));
      
      // Refresh to sync with backend
      setTimeout(fetchStrategies, 500);
    } catch (error) {
      console.error('Delete strategy error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete strategy');
    }
  };

  const duplicateStrategy = async (strategyId) => {
    try {
      await axios.post(`${API}/strategies/${strategyId}/duplicate`, {}, { withCredentials: true });
      toast.success('Strategy duplicated');
      fetchStrategies();
    } catch (error) {
      toast.error('Failed to duplicate strategy');
    }
  };

  const handleSaveStrategy = () => {
    setShowBuilder(false);
    setSelectedStrategy(null);
    fetchStrategies();
  };

  if (showBuilder) {
    return (
      <StrategyBuilder
        strategy={selectedStrategy}
        onSave={handleSaveStrategy}
        onCancel={() => {
          setShowBuilder(false);
          setSelectedStrategy(null);
        }}
      />
    );
  }

  return (
    <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="strategies-tab-content">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          My Strategies
        </h2>
        <Button onClick={createNewStrategy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-strategy-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Strategy
        </Button>
      </div>

      {strategies.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No strategies created yet</p>
          <Button onClick={createNewStrategy} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Strategy
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {strategies.map((strategy) => (
            <div key={strategy.id} className="p-6 bg-slate-50 rounded-xl border border-gray-200 hover:border-indigo-300 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{strategy.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{strategy.description || 'No description'}</p>
                </div>
                <div className="flex items-center gap-1">
                  {strategy.is_deployed && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      Active
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => editStrategy(strategy)}
                  variant="outline"
                  size="sm"
                  data-testid={`edit-strategy-${strategy.id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => duplicateStrategy(strategy.id)}
                  variant="outline"
                  size="sm"
                  data-testid={`duplicate-strategy-${strategy.id}`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
                <Button
                  onClick={() => deleteStrategy(strategy.id)}
                  variant="destructive"
                  size="sm"
                  data-testid={`delete-strategy-${strategy.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default StrategiesTab;
